import { Box, Grid, GridProps, Typography } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { WarningIcon } from '../../../../icons';
import { TooltipComponent } from '../../../atomic/feedback';

export type DataGridElementProps = {
  value: string | number | boolean | undefined | null | string[];
  title: string;
  type?: 'string' | 'boolean' | 'url';
  isHidden?: boolean;
  required?: boolean;
  missingMessage?: string;
} & Pick<GridProps, 'lg' | 'md' | 'sm' | 'xl' | 'xs' | 'sx'>;

export const DataGridElement: FC<DataGridElementProps> = ({
  value,
  title,
  type,
  isHidden,
  required,
  missingMessage,
  sx,
  ...gridSizes
}) => {
  const { t } = useTranslation('common');

  const renderValue = () => {
    if (type === 'boolean') {
      return value ? t('YES') : t('NO');
    }

    return value ? value : '-';
  };

  if (isHidden) {
    return <></>;
  }

  return (
    <Grid item sx={sx} xs={gridSizes?.xs ?? 12} md={gridSizes?.xs ?? 6} {...gridSizes}>
      <Box
        sx={{
          backgroundColor: 'grey.50',
          borderRadius: '12px',
          padding: '12px 16px'
        }}
      >
        <Box display="flex">
          <Typography
            lineHeight="24px"
            flex={1}
            variant="body2"
            sx={(theme) => ({
              fontSize: '0.875rem',
              marginBottom: '1px',
              color: theme.palette.grey['500'],
              textOverflow: 'ellipsis',
              overflow: 'hidden'
            })}
          >
            {title}
          </Typography>
          {required && missingMessage && (
            <TooltipComponent title={missingMessage} placement="left" arrow>
              <Box>
                <WarningIcon color="grey.500" />
              </Box>
            </TooltipComponent>
          )}
        </Box>
        <Typography
          variant="body1"
          component={type === 'url' ? 'a' : 'p'}
          href={type === 'url' ? (value as string) : undefined}
          target={type === 'url' ? '_blank' : undefined}
          sx={{
            fontSize: '1.125rem',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            fontWeight: 'medium'
          }}
        >
          {renderValue()}
        </Typography>
      </Box>
    </Grid>
  );
};

export type DataGridElementType = React.ReactElement<DataGridElementProps>;
